import { CURRENT_LEGAL_MODE_LOCAL_STORAGE_KEY } from "hassibot/current-legal-entity.constants";
import { get } from "hassibot/local-storage";

import { LegalMode } from "./services_v2/common/types";

/**
 * Non-reactive read of the current legal mode: the value is re-read from
 * localStorage on EVERY call, never frozen at module evaluation.
 *
 * INVARIANT: the mode is READ at access time, never baked at module evaluation.
 * Any module-scope value that depends on the mode must go through `perMode()`
 * (see `legal-mode-lazy.ts`), so importing this module — even
 * from the eager bootstrap — is harmless.
 *
 * Callers must still only READ the mode after the legal entity has been seeded
 * into localStorage (see `CurrentLegalEntityProvider`): a pre-seed read observes
 * `null` and would break the first-open branding / i18n namespace — silently
 * for a CONTRACTOR user, whose wordings fall back to the mandatary defaults.
 * Hence: throw in dev to surface the offending call site, warn + Sentry once
 * per session in prod to keep that transient branding bug observable.
 */
let hasReportedPreSeedRead = false;

const reportPreSeedRead = (): void => {
  const message =
    "[legal-mode] legal mode read before the localStorage seed: read the mode only " +
    "after CurrentLegalEntityProvider has seeded the legal entity.";
  if (import.meta.env.MODE === "development") {
    throw new Error(message);
  }
  if (import.meta.env.MODE === "test" || hasReportedPreSeedRead) return;
  hasReportedPreSeedRead = true;
  console.warn(message);
  void import("@sentry/react").then(Sentry => Sentry.captureMessage(message)).catch(() => {});
};

// The stored mode is cached in memory after the first successful read: the
// per-mode proxies (legal-mode-lazy.ts) resolve it on every property access,
// and a localStorage hit per access is wasted work for a value that only
// changes through an entity switch — every write path goes through
// invalidateCurrentLegalModeCache(). The cache also pins the mode to THIS
// tab's React tree: another tab switching entity rewrites the shared
// localStorage, but this tab keeps rendering consistently under its own mode
// until its own switch or reload — the same per-tab stability the full page
// reload used to give. A pre-seed `null` is never cached, so early reads keep
// polling localStorage until the seed lands.
let cachedLegalMode: LegalMode | null = null;
let hasCachedLegalMode = false;

export const getCurrentLegalMode = (): LegalMode | null => {
  if (hasCachedLegalMode) return cachedLegalMode;
  const mode = get(CURRENT_LEGAL_MODE_LOCAL_STORAGE_KEY) as LegalMode | null;
  if (mode === null) {
    reportPreSeedRead();
    return null;
  }
  cachedLegalMode = mode;
  hasCachedLegalMode = true;
  return mode;
};

/**
 * Drop the in-memory mode cache so the next read hits localStorage again.
 * MUST be called by every code path that rewrites the stored legal mode
 * (entity switch, seed — see CurrentLegalEntityProvider).
 */
export const invalidateCurrentLegalModeCache = (): void => {
  cachedLegalMode = null;
  hasCachedLegalMode = false;
};
